import React from 'react';
import type { RouteComponentProps } from 'react-router-dom';
import { Redirect, Route, Switch } from 'react-router-dom';
import { useObservable } from 'react-use-observable';

import Clarity from '@microsoft/clarity';
import Layout from 'components/Layout';
import PermissionRoute from 'components/PermissionRoute';
import { CLARITY_ID, VERTICAL_URL } from 'settings';
import Affiliate from 'v2/components/Pages/Affiliate';
import Affiliations from 'v2/components/Pages/Affiliations';
import { ConfigAPI } from 'v2/components/Pages/ConfigAPI';
import { Connect as NewConnect } from 'v2/components/Pages/Connect';
import { CreateProductOffer } from 'v2/components/Pages/CreateProductOffer';
import { EditProductOffer } from 'v2/components/Pages/EditProductOffer';
import ErrorBoundary from 'v2/components/Pages/ErrorBoundary';
import FinancialStatement from 'v2/components/Pages/FinancialStatement';
import Help from 'v2/components/Pages/Help';
import Leads from 'v2/components/Pages/Leads';
import Notifications from 'v2/components/Pages/Notifications';
import Orders from 'v2/components/Pages/Orders';
import Quickstart from 'v2/components/Pages/Quickstart';
import Rating from 'v2/components/Pages/Rating';
import { Reports } from 'v2/components/Pages/Reports';
import { AffiliateCommissions } from 'v2/components/Pages/Reports/AffiliateCommissions';
import Chargeback from 'v2/components/Pages/Reports/Chargeback';
import InterruptionReport from 'v2/components/Pages/Reports/Interruption';
import { PartnerCommissions } from 'v2/components/Pages/Reports/PartnerCommissions';
import { Refunds } from 'v2/components/Pages/Reports/Refunds';
import RequestManagement from 'v2/components/Pages/RequestManagement';
import Shopping from 'v2/components/Pages/Shopping';
import { ChangePlan } from 'v2/components/Pages/Shopping/Subscription/ChangePlan';
// import { SubscriptionPlan } from 'v2/components/Pages/SubscriptionPlan';
import TransferHistory from 'v2/components/Pages/TransferHistory';
import Webhook2 from 'v2/components/Pages/Webhook2';
import WhiteList from 'v2/components/Pages/WhiteList';
import Zapier from 'v2/components/Pages/Zapier';
import ZapierNew from 'v2/components/Pages/Zapier/New';
import GlobalLoading from 'v2/components/Shared/GlobalLoading';
import withPartnerOnly from 'v2/components/Shared/withPartnerOnly';
import type { IUserData } from 'v2/interfaces/user';
import analyticsService from 'v2/services/analytics';
import eduzzAccountsService from 'v2/services/eduzzAccount';

import NotFound from '../NotFound';
import AccessPolicy from './AccessPolicy';
import Connect from './Connect';
import Dashboard from './Dashboard';
import FiscalDocuments from './FiscalDocuments';
import Messages from './Messages';
import Newzz from './Newzz';
import ProducerDetail from './ProducerDetail';
import Product from './Product';
import Profile from './Profile';
import RecoveryMessages from './RecoveryMessages';

const PartnerOnlyRoute = withPartnerOnly(Route);
const PartnerOnlyPermissionRoute = withPartnerOnly(PermissionRoute);

Clarity.init(CLARITY_ID);

const MyCampaigns = React.lazy(() => import('./MyCampaigns'));

const Producer = () => {
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
  const [betaModules] = useObservable(() => eduzzAccountsService.getBetaModules(), []);

  React.useEffect(() => {
    if (!user || !betaModules?.length) return;
    if (analyticsService.isGTMInitialized) return;

    const userCurrentBelt = user?.current_elite_level?.name || 'White Belt';

    analyticsService.initializeGTM(
      user.id,
      eduzzAccountsService.getBeltById(user.current_elite_level.id),
      betaModules.join(', ')
    );
    Clarity.identify(user.email, undefined, undefined, user.full_name);
    Clarity.setTag('belt', userCurrentBelt);
  }, [user, betaModules]);

  if (!user) return null;

  const isPartner = user?.acceptence_terms;
  const isProducer = user?.producer;
  const isAffiliate = user?.affiliate;
  const isCoProducer = user?.coproducer;
  setExternalPlugins(user);

  const partnerRedirectPath = !isProducer && !isAffiliate && !isCoProducer ? 'quickstart' : 'dashboard';

  return (
    <Layout>
      <ErrorBoundary>
        <Switch>
          {isPartner && <Redirect exact path='/' to={`/producer/${partnerRedirectPath}`} />}
          {!isPartner && <Redirect exact path='/' to='/producer/shopping' />}
          <PartnerOnlyRoute path='/producer/dashboard' component={Dashboard} />
          <PartnerOnlyRoute path='/producer/quickstart' component={Quickstart} />
          <Redirect path='/producer/mysubscriptions' to='/producer/subscriptions' />
          <Redirect path='/producer/myleads' to='/producer/leads' />
          <Redirect path='/producer/coupons' to='/producer/product/myproducts' />
          <PartnerOnlyRoute
            path='/producer/sales'
            render={() => {
              window.location.href = `${VERTICAL_URL}/sales`;
              return <GlobalLoading />;
            }}
          />
          <PartnerOnlyPermissionRoute path='/producer/leads' action='myeduzz:meus-leads:visualizar' component={Leads} />
          <PartnerOnlyPermissionRoute
            path='/producer/orders'
            action='myeduzz:minhas-entregas:visualizar'
            component={Orders}
          />
          <PartnerOnlyPermissionRoute
            exact
            path='/producer/subscriptions'
            action='myeduzz:meus-contratos:visualizar'
            render={() => {
              window.location.href = `${VERTICAL_URL}/subscriptions`;
              return <GlobalLoading />;
            }}
          />
          <PartnerOnlyPermissionRoute
            path='/producer/myaffiliates'
            action='myeduzz:meus-afiliados:visualizar'
            component={Affiliate}
          />
          <PartnerOnlyPermissionRoute
            path='/producer/product/:productId/offers/create'
            action='myeduzz:meus-produtos:visualizar'
            component={CreateProductOffer}
          />
          <PartnerOnlyPermissionRoute
            path='/producer/product/:productId/offers/:offerId/edit'
            action='myeduzz:meus-produtos:visualizar'
            component={EditProductOffer}
          />
          <PartnerOnlyPermissionRoute
            path='/producer/product'
            action='myeduzz:meus-produtos:visualizar'
            component={Product}
          />
          <PartnerOnlyPermissionRoute
            path='/producer/affiliations'
            action='myeduzz:meus-afiliados:visualizar'
            component={Affiliations}
          />
          <PartnerOnlyPermissionRoute
            path='/producer/campaigns/:productId?/:tab?'
            action='myeduzz:links:visualizar'
            component={MyCampaigns}
          />
          <Route path='/producer/profile' component={Profile} />

          <PartnerOnlyRoute
            path='/producer/invoice/details'
            render={(routeProps: RouteComponentProps) => {
              const { pathname } = routeProps.location;
              const invoiceId = parseInt(pathname.split('/').pop());
              const redirectTo = invoiceId ? `/invoices/${invoiceId}` : '';

              window.location.href = `${VERTICAL_URL}/sales${redirectTo}`;

              return <GlobalLoading />;
            }}
          />

          <Redirect path='/producer/financial' to='/producer/financial-statement' />
          <PartnerOnlyPermissionRoute
            path='/producer/financial-statement/transfer-history'
            action='myeduzz:financeiro:visualizar'
            component={TransferHistory}
          />
          <PartnerOnlyPermissionRoute
            path='/producer/financial-statement'
            action='myeduzz:financeiro:visualizar'
            component={FinancialStatement}
          />
          <PartnerOnlyPermissionRoute
            path='/producer/access'
            action='no-access-control-permission'
            component={AccessPolicy}
          />
          <PartnerOnlyRoute path='/producer/rvpmessages/:productId?' component={RecoveryMessages} />
          <PartnerOnlyRoute path='/producer/rating' component={Rating} />
          <PartnerOnlyPermissionRoute
            path='/producer/config-api'
            action='no-access-control-permission'
            component={ConfigAPI}
          />
          <PartnerOnlyPermissionRoute
            path='/producer/reports/sales/commissions/partners'
            action={'myeduzz:reports:visualizar'}
            component={PartnerCommissions}
          />
          <PartnerOnlyPermissionRoute
            path='/producer/reports/sales/commissions/affiliates'
            action={'myeduzz:reports:visualizar'}
            component={AffiliateCommissions}
          />
          <PartnerOnlyPermissionRoute
            path='/producer/reports/sales/refunds'
            action={'myeduzz:reports:visualizar'}
            component={Refunds}
          />
          <PartnerOnlyPermissionRoute
            path='/producer/reports/sales/cancel'
            action={'myeduzz:reports:visualizar'}
            component={InterruptionReport}
          />
          <PartnerOnlyPermissionRoute
            path='/producer/reports/sales'
            action={'myeduzz:reports:visualizar'}
            component={Reports}
          />
          <PartnerOnlyPermissionRoute
            path='/producer/notifications/:type?'
            action={[
              'myeduzz:minhas-vendas:visualizar',
              'myeduzz:meus-produtos:visualizar',
              'myeduzz:request-management:visualizar'
            ]}
            component={Notifications}
          />
          <PartnerOnlyPermissionRoute
            path='/producer/whitelist'
            action='myeduzz:whitelist:visualizar'
            component={WhiteList}
          />
          <Route path='/producer/detail/:producerId' component={ProducerDetail} />
          <PermissionRoute path='/producer/help' action='myeduzz:ajuda:visualizar' component={Help} />
          <PartnerOnlyPermissionRoute
            path='/producer/messages/:id?'
            action='myeduzz:meus-afiliados:visualizar'
            component={Messages}
          />
          <Route path='/producer/shopping/:tabName(subscriptions|products)?' component={Shopping} />
          <Route path='/producer/subscriptions/:subscriptionId/change-plan' component={ChangePlan} />
          <PartnerOnlyPermissionRoute
            path='/producer/request-management'
            action={['myeduzz:inside-sales:visualizar', 'myeduzz:request-management:visualizar']}
            component={RequestManagement}
          />
          <PartnerOnlyPermissionRoute
            path='/producer/cdf'
            action='myeduzz:cdf:visualizar'
            component={FiscalDocuments}
          />
          <PartnerOnlyPermissionRoute
            path='/producer/chargebacks'
            action='myeduzz:chargeback:visualizar'
            component={Chargeback}
          />
          <PartnerOnlyPermissionRoute
            path='/producer/connect'
            action='myeduzz:connect:visualizar'
            component={Connect}
          />
          <PartnerOnlyPermissionRoute
            path='/producer/new-connect'
            action='myeduzz:connect:visualizar'
            component={NewConnect}
          />

          <PartnerOnlyPermissionRoute
            path='/producer/webhook'
            action='myeduzz:webhook:visualizar'
            component={Webhook2}
          />

          <Route path='/producer/news' component={Newzz} />
          <PartnerOnlyRoute path='/producer/zapier' component={Zapier} />
          <PartnerOnlyRoute path='/producer/zapier-new' component={ZapierNew} />
          <PermissionRoute path='/producer/profile' action='no-access-control-permission' component={Profile} />
          <Route path='*' component={NotFound} />
        </Switch>
      </ErrorBoundary>
    </Layout>
  );
};

const setExternalPlugins = (user: IUserData): void => {
  if (typeof window.hj !== 'undefined') {
    const tags = [`${user.id}`, user.current_elite_level?.name];

    window.hj('tagRecording', tags);
  }
};

export default Producer;
